<template>
    <subscription-dropdown
        aria-haspopup
        :is-impersonating="isImpersonating"
        @open="handleSubscriptionOpen"
        @close="handleSubscriptionClose" />
    <div class="right-options">
        <pendo-tools-dropdown
            ref="pendoTools"
            aria-haspopup
            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS)"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.PENDO_TOOLS) && 'page'"
            :is-open="isDropdownOpen('pendoTools')"
            @open="handleDropdownOpen('pendoTools')"
            @close="handleDropdownClose('pendoTools')"
            @focus-trigger="handleFocusTrigger('pendoTools')"
            @select="closeAllDropdowns" />
        <pendo-products-dropdown
            ref="pendoProducts"
            aria-haspopup
            :is-open="isDropdownOpen('pendoProducts')"
            @open="handleDropdownOpen('pendoProducts')"
            @close="handleDropdownClose('pendoProducts')"
            @focus-trigger="handleFocusTrigger('pendoProducts')"
            @select="closeAllDropdowns" />
        <settings-dropdown
            ref="settings"
            aria-haspopup
            :selected="navItemActive(TOP_LEVEL_NAV_OPTIONS.SETTINGS)"
            :aria-current="navItemActive(TOP_LEVEL_NAV_OPTIONS.SETTINGS) && 'page'"
            :is-open="isDropdownOpen('settings')"
            @open="handleDropdownOpen('settings')"
            @close="handleDropdownClose('settings')"
            @focus-trigger="handleFocusTrigger('settings')"
            @select="closeAllDropdowns" />
        <notifications-dropdown
            v-if="hasNotificationsAccess"
            ref="notifications"
            aria-haspopup
            :is-open="isDropdownOpen('notifications')"
            @open="handleDropdownOpen('notifications')"
            @close="handleDropdownClose('notifications')"
            @focus-trigger="handleFocusTrigger('notifications')"
            @select="closeAllDropdowns" />
        <user-dropdown
            ref="user"
            aria-haspopup
            :is-open="isDropdownOpen('user')"
            @open="handleDropdownOpen('user')"
            @close="handleDropdownClose('user')"
            @focus-trigger="handleFocusTrigger('user')"
            @select="closeAllDropdowns" />
    </div>
    <pendo-popover
        ref="moreOptions"
        class="right-options__popover"
        placement="bottom-end"
        trigger="manual"
        :open="isMoreOptionsOpen"
        :popover-class="['right-options__popover-wrapper']"
        @update:open="handleMoreOptionsToggle">
        <pendo-button
            v-pendo-tooltip="{
                content: $t('topNav.More options'),
                arrow: true,
            }"
            class="top-nav__trigger top-nav__button-trigger"
            :class="{ 'is-open': isMoreOptionsOpen }"
            type="tertiary"
            size="small"
            pendo-analytics="more-options"
            aria-haspopup
            :aria-label="$t('topNav.More options')"
            @click="handleMoreOptionsToggle(!isMoreOptionsOpen)"
            @keydown.space.prevent="handleMoreOptionsToggle(!isMoreOptionsOpen)"
            @keydown.enter.prevent="handleMoreOptionsToggle(!isMoreOptionsOpen)">
            <pendo-icon
                type="more-horizontal"
                size="20" />
        </pendo-button>
        <template #popover>
            <div
                ref="moreOptionsContent"
                class="right-options__popover-content"
                @keydown.escape.prevent="handleMoreOptionsEscape">
                <pendo-tools-dropdown
                    ref="moreOptionsPendoTools"
                    class="more-options__popover"
                    aria-haspopup
                    prevent-tab-key-navigation
                    :is-open="isPopoverDropdownOpen('pendoTools')"
                    @open="handlePopoverDropdownOpen('pendoTools')"
                    @close="handlePopoverDropdownClose('pendoTools')"
                    @focus-trigger="handleFocusTrigger('moreOptionsPendoTools')"
                    @select="closeAllDropdowns"
                    @left="handleMoreOptionsKeydown('left')"
                    @right="handleMoreOptionsKeydown('right')" />
                <pendo-products-dropdown
                    ref="moreOptionsPendoProducts"
                    class="more-options__popover"
                    aria-haspopup
                    prevent-tab-key-navigation
                    :is-open="isPopoverDropdownOpen('pendoProducts')"
                    @open="handlePopoverDropdownOpen('pendoProducts')"
                    @close="handlePopoverDropdownClose('pendoProducts')"
                    @focus-trigger="handleFocusTrigger('moreOptionsPendoProducts')"
                    @select="closeAllDropdowns"
                    @left="handleMoreOptionsKeydown('left')"
                    @right="handleMoreOptionsKeydown('right')" />
                <settings-dropdown
                    ref="moreOptionsSettings"
                    class="more-options__popover"
                    aria-haspopup
                    prevent-tab-key-navigation
                    :is-open="isPopoverDropdownOpen('settings')"
                    @open="handlePopoverDropdownOpen('settings')"
                    @close="handlePopoverDropdownClose('settings')"
                    @focus-trigger="handleFocusTrigger('moreOptionsSettings')"
                    @select="closeAllDropdowns"
                    @left="handleMoreOptionsKeydown('left')"
                    @right="handleMoreOptionsKeydown('right')" />
                <notifications-dropdown
                    v-if="hasNotificationsAccess"
                    ref="moreOptionsNotifications"
                    class="more-options__popover"
                    aria-haspopup
                    prevent-tab-key-navigation
                    :is-open="isPopoverDropdownOpen('notifications')"
                    @open="handlePopoverDropdownOpen('notifications')"
                    @close="handlePopoverDropdownClose('notifications')"
                    @focus-trigger="handleFocusTrigger('moreOptionsNotifications')"
                    @select="closeAllDropdowns"
                    @left="handleMoreOptionsKeydown('left')"
                    @right="handleMoreOptionsKeydown('right')" />
                <user-dropdown
                    ref="moreOptionsUser"
                    class="more-options__popover"
                    aria-haspopup
                    prevent-tab-key-navigation
                    :is-open="isPopoverDropdownOpen('user')"
                    @open="handlePopoverDropdownOpen('user')"
                    @close="handlePopoverDropdownClose('user')"
                    @focus-trigger="handleFocusTrigger('moreOptionsUser')"
                    @select="closeAllDropdowns"
                    @left="handleMoreOptionsKeydown('left')"
                    @right="handleMoreOptionsKeydown('right')" />
            </div>
        </template>
    </pendo-popover>
</template>

<script>
import SubscriptionDropdown from '@/top-nav/components/subscriptions/subscription-dropdown.vue';
import UserDropdown from '@/top-nav/components/user-dropdown/user-dropdown.vue';
import PendoToolsDropdown from '@/top-nav/components/pendo-tools/pendo-tools-dropdown.vue';
import PendoProductsDropdown from '@/top-nav/components/pendo-products/pendo-products-dropdown.vue';
import NotificationsDropdown from '@/top-nav/components/notifications/notifications-dropdown.vue';
import SettingsDropdown from '@/top-nav/components/settings/settings-dropdown.vue';
import { PendoPopover, PendoButton, PendoIcon, PendoTooltip } from '@pendo/components';

import { TOP_LEVEL_NAV_OPTIONS } from '@/constants/nav';
import { isOptionActive } from '@/side-nav/helpers/side-nav.helper';

export default {
    name: 'TopNavRightOptionsWrapper',
    components: {
        SubscriptionDropdown,
        UserDropdown,
        PendoToolsDropdown,
        PendoProductsDropdown,
        NotificationsDropdown,
        SettingsDropdown,
        PendoPopover,
        PendoButton,
        PendoIcon,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        isImpersonating: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            TOP_LEVEL_NAV_OPTIONS,
            largeScreenDropdownStates: {
                subscriptions: false,
                pendoProducts: false,
                pendoTools: false,
                notifications: false,
                settings: false,
                user: false,
            },
            smallScreenDropdownStates: {
                subscriptions: false,
                moreOptions: false,
            },
            popoverDropdownStates: {
                pendoProducts: false,
                pendoTools: false,
                notifications: false,
                settings: false,
                user: false,
            },
        };
    },
    computed: {
        hasNotificationsAccess () {
            return this.$subFlag('veltIntegration');
        },
        activeNavOption () {
            return this.$route?.meta?.activeNavOption || {};
        },
        isMoreOptionsOpen () {
            return this.smallScreenDropdownStates.moreOptions;
        },
    },
    created () {
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount () {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize () {
            this.closeAllDropdowns();
        },
        closeAllDropdowns () {
            Object.keys(this.largeScreenDropdownStates).forEach((id) => {
                this.largeScreenDropdownStates[id] = false;
            });
            Object.keys(this.smallScreenDropdownStates).forEach((id) => {
                this.smallScreenDropdownStates[id] = false;
            });
            Object.keys(this.popoverDropdownStates).forEach((id) => {
                this.popoverDropdownStates[id] = false;
            });
        },
        closePopoverDropdowns () {
            Object.keys(this.popoverDropdownStates).forEach((id) => {
                this.popoverDropdownStates[id] = false;
            });
        },
        handleFocusTrigger (ref) {
            const focusTarget = this.$refs[ref]?.$el?.querySelector('.top-nav__trigger');
            focusTarget?.focus();
        },
        navItemActive (navItemName) {
            return isOptionActive(navItemName, this.activeNavOption.topOptionName);
        },
        isDropdownOpen (dropdownId) {
            return this.largeScreenDropdownStates[dropdownId] || false;
        },
        handleDropdownOpen (dropdownId) {
            Object.keys(this.largeScreenDropdownStates).forEach((id) => {
                this.largeScreenDropdownStates[id] = id === dropdownId;
            });
        },
        handleDropdownClose (dropdownId) {
            this.largeScreenDropdownStates[dropdownId] = false;
        },
        handleMoreOptionsToggle (open) {
            if (open) {
                this.smallScreenDropdownStates.subscriptions = false;
                this.smallScreenDropdownStates.moreOptions = true;
                setTimeout(() => {
                    const firstOption = this.$refs.moreOptionsContent?.querySelector('.more-options__popover');
                    if (firstOption) {
                        firstOption.querySelector('.top-nav__trigger').focus();
                    }
                }, 50);
            } else {
                this.smallScreenDropdownStates.moreOptions = false;
                this.closePopoverDropdowns();
                this.handleFocusTrigger('moreOptions');
            }
        },
        isPopoverDropdownOpen (dropdownId) {
            return this.popoverDropdownStates[dropdownId] || false;
        },
        handlePopoverDropdownOpen (dropdownId) {
            Object.keys(this.popoverDropdownStates).forEach((id) => {
                this.popoverDropdownStates[id] = id === dropdownId;
            });
        },
        handlePopoverDropdownClose (dropdownId) {
            this.popoverDropdownStates[dropdownId] = false;
        },
        handleSubscriptionOpen () {
            if (window.innerWidth <= 1200) {
                this.smallScreenDropdownStates.moreOptions = false;
                this.closePopoverDropdowns();
                this.smallScreenDropdownStates.subscriptions = true;
            } else {
                this.handleDropdownOpen('subscriptions');
            }
        },
        handleSubscriptionClose () {
            if (window.innerWidth <= 1200) {
                this.smallScreenDropdownStates.subscriptions = false;
            } else {
                this.handleDropdownClose('subscriptions');
            }
        },
        handleMoreOptionsEscape () {
            this.smallScreenDropdownStates.moreOptions = false;
            this.handleFocusTrigger('moreOptions');
        },
        getMoreOptionsTriggers () {
            return Array.from(this.$refs.moreOptionsContent?.querySelectorAll('.more-options__popover') || []);
        },
        getCurrentFocusedTriggerIndex () {
            const triggers = this.getMoreOptionsTriggers();

            if (!triggers || triggers.length === 0) {
                return -1;
            }

            return triggers.findIndex((trigger) => trigger.contains(document.activeElement));
        },
        handleMoreOptionsKeydown (direction) {
            const triggers = this.getMoreOptionsTriggers();
            const currentIndex = this.getCurrentFocusedTriggerIndex();

            if (currentIndex === -1 || triggers.length === 1) {
                return;
            }

            let nextIndex;

            if (direction === 'left') {
                nextIndex = currentIndex === 0 ? triggers.length - 1 : currentIndex - 1;
            } else if (direction === 'right') {
                nextIndex = currentIndex === triggers.length - 1 ? 0 : currentIndex + 1;
            }

            triggers[nextIndex].querySelector('.top-nav__trigger').focus();
        },
    },
};
</script>

<style lang="less" scoped>
@import '@/side-nav/nav.style.less';

.right-options {
    display: flex;
    gap: 6px;
    align-items: center;

    &__popover {
        display: none;

        .screen-size(@zero, @x-large, {
            display: block;
        });
    }

    &__popover-content {
        display: flex;
        gap: 6px;
    }

    .screen-size(@zero, @x-large, {
        display: none;
    });
}
</style>

<style lang="less">
.right-options__popover-wrapper {
    background-color: var(--palette-gray-5);
}
</style>